import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';

const NASServer = () => {
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewDirModal, setShowNewDirModal] = useState(false);
  const [newDirName, setNewDirName] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFileSize, setUploadFileSize] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadCurrent, setUploadCurrent] = useState(0);
  const [nasInfo, setNasInfo] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [copied, setCopied] = useState(null);
  const [usage, setUsage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchMode, setSearchMode] = useState(false);
  const [searching, setSearching] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [viewMode, setViewMode] = useState('list');
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [overwriteConfirm, setOverwriteConfirm] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const fileInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get('/api/users/me/', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setIsAdmin(!!res.data?.is_admin))
      .catch(() => setIsAdmin(false));
  }, []);

  const fetchUsage = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get('/api/nas/usage', { headers: { Authorization: `Bearer ${token}` } });
      setUsage(res.data);
    } catch (_) { setUsage(null); }
  }, []);

  const fetchList = async (path) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const p = (path ?? currentPath).replace(/^\/+/, '');
      const res = await axios.get('/api/nas/list', {
        params: { path: p || undefined },
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(res.data.items || []);
      setCurrentPath(res.data.current_path ?? '');
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Не удалось загрузить список'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchNasInfo = async (openModal = true) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get('/api/nas/info', { headers: { Authorization: `Bearer ${token}` } });
      setNasInfo(res.data);
      if (openModal) setShowInfo(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Не удалось загрузить данные подключения'));
    }
  };

  useEffect(() => { fetchList(''); fetchUsage(); }, []);
  useEffect(() => { const t = setTimeout(() => { setSuccess(''); setError(''); }, 5000); return () => clearTimeout(t); }, [success, error]);

  const doSearch = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token || !searchQuery.trim()) { setSearchResults([]); setSearchMode(false); return; }
    setSearching(true);
    setSearchMode(true);
    try {
      const res = await axios.get('/api/nas/search', {
        params: { q: searchQuery.trim(), path: currentPath, limit: 100 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data.items || []);
    } catch (_) { setSearchResults([]); }
    setSearching(false);
  }, [searchQuery, currentPath]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!searchQuery.trim()) { setSearchResults([]); setSearchMode(false); return; }
    searchTimeoutRef.current = setTimeout(doSearch, 400);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery, doSearch]);

  const displayItems = searchMode ? searchResults : items;
  const sortedItems = useMemo(() => {
    const list = [...displayItems];
    const key = sortBy === 'name' ? 'name' : sortBy === 'size' ? 'size' : 'mtime';
    list.sort((a, b) => {
      let va = key === 'name' ? (a.name || '').toLowerCase() : (a[key] ?? (key === 'size' ? 0 : 0));
      let vb = key === 'name' ? (b.name || '').toLowerCase() : (b[key] ?? (key === 'size' ? 0 : 0));
      if (key !== 'name') { if (va < vb) return sortOrder === 'asc' ? -1 : 1; if (va > vb) return sortOrder === 'asc' ? 1 : -1; return 0; }
      return sortOrder === 'asc' ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
    });
    return list;
  }, [displayItems, sortBy, sortOrder]);

  const toggleSelect = (path) => {
    setSelectedPaths(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };
  const selectAll = () => {
    const paths = sortedItems.filter(i => !i.is_dir).map(i => i.path);
    setSelectedPaths(prev => prev.length === paths.length ? [] : paths);
  };
  const fetchTrash = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get('/api/nas/trash', { headers: { Authorization: `Bearer ${token}` } });
      setTrashItems(res.data.items || []);
    } catch (_) { setTrashItems([]); }
  }, []);

  const openDir = (path) => {
    setCurrentPath(path);
    fetchList(path);
  };

  const breadcrumbs = currentPath ? ['', ...currentPath.split('/').filter(Boolean)] : [''];

  const handleCreateDir = async (e) => {
    e.preventDefault();
    if (!newDirName.trim()) return;
    const token = localStorage.getItem('token');
    const fullPath = currentPath ? `${currentPath}/${newDirName.trim()}` : newDirName.trim();
    try {
      await axios.post('/api/nas/dirs', { path: fullPath }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess('Каталог создан');
      setShowNewDirModal(false);
      setNewDirName('');
      fetchList(currentPath);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Ошибка создания каталога'));
    }
  };

  const uploadOne = async (file, uploadPath, overwrite = true) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`/api/nas/upload?path=${encodeURIComponent(uploadPath)}&overwrite=${overwrite}`, file, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Filename': file.name,
          'Content-Type': 'application/octet-stream'
        },
        timeout: 0,
        onUploadProgress: (ev) => {
          if (ev.total && ev.total > 0) {
            setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          } else {
            setUploadProgress(ev.loaded ? 50 : 0);
          }
        }
      });
      return true;
    } catch (err) {
      if (err.response?.status === 409) throw new Error('EXISTS');
      const d = err.response?.data?.detail;
      throw new Error(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Ошибка загрузки'));
    }
  };

  const handleUpload = async (e) => {
    const files = e.target?.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    const uploadPath = (currentPath === '.' || currentPath === undefined || currentPath === null) ? '' : currentPath;
    setError('');
    setUploadTotal(files.length);
    setUploadCurrent(0);
    setUploading(true);
    let done = 0;
    for (const file of files) {
      setUploadFileName(file.name);
      setUploadFileSize(file.size);
      setUploadProgress(0);
      try {
        await uploadOne(file, uploadPath, true);
        done++;
        setUploadCurrent(done);
        setSuccess(`Загружено ${done} из ${files.length}`);
      } catch (err) {
        if (err.message === 'EXISTS') {
          setOverwriteConfirm({ file, uploadPath, onConfirm: (overwrite) => {
            setOverwriteConfirm(null);
            uploadOne(file, uploadPath, overwrite).then(() => { setUploadCurrent(++done); fetchList(currentPath); fetchUsage(); setSuccess('Файл загружен'); setUploading(false); setUploadProgress(0); setUploadFileName(''); setUploadFileSize(0); setUploadTotal(0); setUploadCurrent(0); }).catch(msg => { setError(msg.message || 'Ошибка'); setUploading(false); });
          } });
          return;
        }
        setError(err.message || 'Ошибка загрузки');
        break;
      }
    }
    setUploading(false);
    setUploadProgress(0);
    setUploadFileName('');
    setUploadFileSize(0);
    setUploadCurrent(0);
    setUploadTotal(0);
    fetchList(currentPath);
    fetchUsage();
    if (done === files.length) setSuccess(`Загружено файлов: ${done}`);
    e.target.value = '';
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!renameTarget || !renameValue.trim()) return;
    const token = localStorage.getItem('token');
    try {
      await axios.put('/api/nas/rename', { path: renameTarget.path, new_name: renameValue.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess('Переименовано');
      setRenameTarget(null);
      setRenameValue('');
      fetchList(currentPath);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Ошибка переименования'));
    }
  };

  const handleDelete = async (softDelete = true) => {
    if (!deleteTarget) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete('/api/nas/remove', { params: { path: deleteTarget.path, soft: softDelete }, headers: { Authorization: `Bearer ${token}` } });
      setSuccess(softDelete ? 'Перемещено в корзину' : (deleteTarget.is_dir ? 'Каталог удалён' : 'Файл удалён'));
      setDeleteTarget(null);
      setSelectedPaths(prev => prev.filter(p => p !== deleteTarget.path));
      fetchList(currentPath);
      fetchUsage();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Ошибка удаления'));
    }
  };

  const handleBatchRemove = async (softDelete = true) => {
    if (selectedPaths.length === 0) return;
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post('/api/nas/batch-remove', { paths: selectedPaths, soft: softDelete }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(`Обработано: ${res.data.removed || 0}${(res.data.errors && res.data.errors.length) ? ', ошибок: ' + res.data.errors.length : ''}`);
      setSelectedPaths([]);
      fetchList(currentPath);
      fetchUsage();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Ошибка удаления'));
    }
  };

  const handleBatchDownload = async () => {
    if (selectedPaths.length === 0) return;
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get('/api/nas/archive', { params: { paths: selectedPaths.join(',') }, headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = (res.headers['content-disposition']?.match(/filename=(.+)/) || [])[1]?.replace(/"/g, '') || 'nas_archive.zip';
      a.click();
      URL.revokeObjectURL(url);
      setSuccess('Архив скачивается');
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Ошибка скачивания'));
    }
  };

  const downloadFile = async (path) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`/api/nas/download`, { params: { path }, headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const name = path.split('/').pop() || 'download';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Ошибка скачивания'));
    }
  };

  const copyToClipboard = (text, key) => {
    const done = () => { setCopied(key); setTimeout(() => setCopied(null), 2000); };
    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
    } else {
      fallbackCopy(text, done);
    }
  };
  const fallbackCopy = (text, done) => {
    let ta = null;
    try {
      ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.setAttribute('readonly', '');
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      if (document.execCommand('copy')) done();
    } catch (e) { /* ignore */ }
    finally {
      if (ta && ta.parentNode) document.body.removeChild(ta);
    }
  };

  const formatSize = (bytes) => {
    if (bytes == null) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatDate = (mtime) => {
    if (mtime == null) return '—';
    return new Date(mtime * 1000).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="nas-server content-area">
      <div className="content-header nas-header">
        <div>
          <h1 className="nas-title">NAS-Сервер</h1>
          <p className="nas-subtitle">Сетевое хранилище: каталоги и файлы с доступом по SMB, FTP, WebDAV, NFS</p>
        </div>
        <div className="header-actions nas-actions">
          <button type="button" className="btn-secondary btn-small" onClick={fetchNasInfo}>Подключение как сетевой диск</button>
          <button type="button" className="btn-secondary btn-small" onClick={() => { setShowHelp(true); if (!nasInfo) fetchNasInfo(false); }}>Помощь</button>
          {isAdmin && <button type="button" className="btn-secondary btn-small" onClick={() => { setShowTrash(true); fetchTrash(); }}>Корзина</button>}
          <button type="button" className="btn-secondary btn-small" onClick={() => setShowNewDirModal(true)}>+ Новый каталог</button>
          <button type="button" className="btn-success btn-small" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? 'Загрузка…' : 'Загрузить файл(ы)'}
          </button>
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} multiple onChange={handleUpload} />
        </div>
      </div>

      {error && <div className="error nas-message">{error}</div>}
      {success && <div className="success nas-message">{success}</div>}

      {usage && usage.success && (
        <div className="nas-usage-block">
          <span className="nas-usage-label">Использование хранилища</span>
          <div className="nas-usage-bar-wrap">
            <div className="nas-usage-bar" style={{ width: (usage.percent || 0) + '%' }} />
          </div>
          <span className="nas-usage-text">{formatSize(usage.used)} из {formatSize(usage.total)} ({Math.round(usage.percent || 0)}%)</span>
        </div>
      )}

      {uploading && (
        <div className="nas-upload-progress-block">
          <div className="nas-upload-progress-header">
            <span className="nas-upload-progress-name">{uploadFileName}</span>
            <span className="nas-upload-progress-size">{formatSize(uploadFileSize)} {uploadTotal > 1 ? ` · ${uploadCurrent}/${uploadTotal}` : ''}</span>
          </div>
          <div className="nas-upload-progress-bar-wrap">
            <div className="nas-upload-progress-bar" style={{ width: uploadProgress + '%' }} />
          </div>
          <div className="nas-upload-progress-percent">{uploadProgress}%</div>
        </div>
      )}

      <div className="nas-toolbar">
        <div className="nas-search-wrap">
          <input type="text" className="nas-search-input" placeholder="Поиск по имени…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          {searchQuery && <button type="button" className="nas-search-clear" onClick={() => setSearchQuery('')} title="Очистить">×</button>}
        </div>
        <div className="nas-view-sort">
          <span className="nas-sort-label">Сортировка:</span>
          <select className="nas-sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">Имя</option>
            <option value="size">Размер</option>
            <option value="mtime">Дата</option>
          </select>
          <button type="button" className="btn-icon-small" onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')} title={sortOrder === 'asc' ? 'По убыванию' : 'По возрастанию'}>{sortOrder === 'asc' ? '↑' : '↓'}</button>
          <span className="nas-view-toggle">
            <button type="button" className={viewMode === 'list' ? 'btn-icon-small active' : 'btn-icon-small'} onClick={() => setViewMode('list')} title="Список">≡</button>
            <button type="button" className={viewMode === 'cards' ? 'btn-icon-small active' : 'btn-icon-small'} onClick={() => setViewMode('cards')} title="Карточки">▦</button>
          </span>
        </div>
      </div>

      <div className="nas-breadcrumb">
        {breadcrumbs.map((part, i) => (
          <span key={i}>
            <button type="button" className="nas-breadcrumb-item" onClick={() => openDir(part === '' ? '' : breadcrumbs.slice(1, i + 1).join('/'))}>
              {part === '' ? 'Хранилище' : part}
            </button>
            {i < breadcrumbs.length - 1 && <span className="nas-breadcrumb-sep"> / </span>}
          </span>
        ))}
      </div>

      {selectedPaths.length > 0 && !searchMode && (
        <div className="nas-batch-bar">
          <span>Выбрано: {selectedPaths.length}</span>
          <button type="button" className="btn-success btn-small" onClick={handleBatchDownload}>Скачать архив</button>
          {isAdmin && (
            <>
              <button type="button" className="btn-secondary btn-small" onClick={() => handleBatchRemove(true)}>В корзину</button>
              <button type="button" className="btn-danger btn-small" onClick={() => handleBatchRemove(false)}>Удалить навсегда</button>
            </>
          )}
          <button type="button" className="btn-secondary btn-small" onClick={() => setSelectedPaths([])}>Снять выделение</button>
        </div>
      )}

      <div
        className="nas-list-wrapper nas-droppable"
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('nas-drag-over'); }}
        onDragLeave={(e) => { e.currentTarget.classList.remove('nas-drag-over'); }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove('nas-drag-over');
          const files = e.dataTransfer?.files;
          if (files?.length && fileInputRef.current) {
            const dt = new DataTransfer();
            for (let i = 0; i < files.length; i++) dt.items.add(files[i]);
            fileInputRef.current.files = dt.files;
            handleUpload({ target: { files: fileInputRef.current.files } });
          }
        }}
      >
        {loading && !searchMode ? (
          <div className="nas-loading">
            <div className="nas-loading-spinner" />
            <span>Загрузка…</span>
          </div>
        ) : searchMode && searching ? (
          <div className="nas-loading"><div className="nas-loading-spinner" /><span>Поиск…</span></div>
        ) : (
          <ul className={viewMode === 'cards' ? 'nas-list nas-list-cards' : 'nas-list'}>
            {!searchMode && viewMode === 'list' && (
              <li className="nas-list-header">
                <span className="nas-list-col-check"><input type="checkbox" checked={sortedItems.length > 0 && sortedItems.filter(i => !i.is_dir).every(i => selectedPaths.includes(i.path))} onChange={selectAll} title="Выбрать все файлы" /></span>
                <span className="nas-list-col-name">Имя</span>
                <span className="nas-list-col-meta">Размер</span>
                <span className="nas-list-col-meta">Дата</span>
              </li>
            )}
            {sortedItems.map((item, idx) => (
              <li key={(item.path || item.name) + idx} className="nas-list-item">
                <div className="nas-list-item-main" onContextMenu={(e) => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, item }); }}>
                  {!searchMode && !item.is_dir && (
                    <span className="nas-list-col-check" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedPaths.includes(item.path)} onChange={() => toggleSelect(item.path)} />
                    </span>
                  )}
                  <button type="button" className="nas-list-item-link" onClick={() => { if (item.is_dir) { openDir(item.path); if (searchMode) { setSearchMode(false); setSearchQuery(''); } } else { if (searchMode) downloadFile(item.path); else setPreviewItem(previewItem?.path === item.path ? null : item); } }}>
                    <span className="nas-list-icon">{item.is_dir ? '📁' : '📄'}</span>
                    <span className="nas-list-name">{item.name}</span>
                    <span className="nas-list-meta">{item.is_dir ? '—' : formatSize(item.size)}</span>
                    <span className="nas-list-meta">{formatDate(item.mtime)}</span>
                  </button>
                  <div className="nas-list-actions">
                    <button type="button" className="btn-icon-small" onClick={(e) => { e.stopPropagation(); setRenameTarget(item); setRenameValue(item.name); }} title="Переименовать">✏️</button>
                    {isAdmin && <button type="button" className="btn-icon-small" onClick={(e) => { e.stopPropagation(); setDeleteTarget(item); }} title="В корзину">🗑️</button>}
                    {!item.is_dir && (
                      <button type="button" className="btn-icon-small" onClick={(e) => { e.stopPropagation(); downloadFile(item.path); }} title="Скачать">⬇️</button>
                    )}
                  </div>
                </div>
              </li>
            ))}
            {sortedItems.length === 0 && !loading && (
              <li className="nas-empty">{searchMode ? 'Ничего не найдено' : 'В этой папке пока ничего нет. Создайте каталог или загрузите файл. Можно перетащить файлы сюда.'}</li>
            )}
          </ul>
        )}
      </div>

      {contextMenu && (
        <>
          <div className="nas-context-overlay" onClick={() => setContextMenu(null)} />
          <div className="nas-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}>
            {contextMenu.item.is_dir ? (
              <button type="button" onClick={() => { openDir(contextMenu.item.path); setContextMenu(null); }}>Открыть</button>
            ) : (
              <button type="button" onClick={() => { downloadFile(contextMenu.item.path); setContextMenu(null); }}>Скачать</button>
            )}
            <button type="button" onClick={() => { setRenameTarget(contextMenu.item); setRenameValue(contextMenu.item.name); setContextMenu(null); }}>Переименовать</button>
            {isAdmin && <button type="button" onClick={() => { setDeleteTarget(contextMenu.item); setContextMenu(null); }}>В корзину</button>}
            {!contextMenu.item.is_dir && <button type="button" onClick={() => { setPreviewItem(contextMenu.item); setContextMenu(null); }}>Свойства</button>}
          </div>
        </>
      )}

      {previewItem && !previewItem.is_dir && (
        <div className="nas-preview-panel">
          <div className="nas-preview-header">
            <span>Свойства: {previewItem.name}</span>
            <button type="button" className="btn-icon-small" onClick={() => setPreviewItem(null)}>×</button>
          </div>
          <div className="nas-preview-body">
            <p><strong>Путь:</strong> {previewItem.path}</p>
            <p><strong>Размер:</strong> {formatSize(previewItem.size)}</p>
            <p><strong>Изменён:</strong> {formatDate(previewItem.mtime)}</p>
            <button type="button" className="btn-success btn-small" onClick={() => downloadFile(previewItem.path)}>Скачать</button>
          </div>
        </div>
      )}

      {overwriteConfirm && (
        <div className="modal-overlay" onClick={() => { setOverwriteConfirm(null); setUploading(false); }}>
          <div className="modal nas-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Файл уже существует</h2>
            <p>Файл «{overwriteConfirm.file.name}» уже есть в этой папке. Перезаписать?</p>
            <div className="modal-actions">
              <button type="button" className="btn-success" onClick={() => overwriteConfirm.onConfirm(true)}>Перезаписать</button>
              <button type="button" className="btn-secondary" onClick={() => { setOverwriteConfirm(null); setUploading(false); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {showNewDirModal && (
        <div className="modal-overlay" onClick={() => setShowNewDirModal(false)}>
          <div className="modal nas-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Новый каталог</h2>
            <form onSubmit={handleCreateDir}>
              <div className="form-group">
                <label>Имя каталога</label>
                <input type="text" value={newDirName} onChange={(e) => setNewDirName(e.target.value)} placeholder="Имя папки" autoFocus title="Введите имя новой папки (латиница, цифры, без спецсимволов)" />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-success">Создать</button>
                <button type="button" className="btn-secondary" onClick={() => { setShowNewDirModal(false); setNewDirName(''); }}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {renameTarget && (
        <div className="modal-overlay" onClick={() => setRenameTarget(null)}>
          <div className="modal nas-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Переименовать</h2>
            <form onSubmit={handleRename}>
              <div className="form-group">
                <label>Новое имя</label>
                <input type="text" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} placeholder={renameTarget.name} title="Новое имя папки или файла" />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-success">Сохранить</button>
                <button type="button" className="btn-secondary" onClick={() => setRenameTarget(null)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Удалить?</h3>
            <p>«{deleteTarget.name}» — переместить в корзину или удалить навсегда?{deleteTarget.is_dir ? ' Для каталога будут затронуты все вложенные объекты.' : ''}</p>
            <div className="confirmation-actions">
              <button type="button" className="btn-secondary btn-small" onClick={() => handleDelete(true)}>В корзину</button>
              <button type="button" className="btn-danger btn-small" onClick={() => handleDelete(false)}>Удалить навсегда</button>
              <button type="button" className="btn-secondary btn-small" onClick={() => setDeleteTarget(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {showTrash && (
        <div className="modal-overlay" onClick={() => setShowTrash(false)}>
          <div className="modal nas-info-modal nas-trash-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Корзина</h2>
            <p className="nas-info-desc">Восстановите или удалите объекты. Очистка корзины удалит всё навсегда.</p>
            <div className="nas-trash-actions">
              <button type="button" className="btn-danger btn-small" onClick={async () => { const token = localStorage.getItem('token'); if (!token) return; try { await axios.delete('/api/nas/trash', { headers: { Authorization: `Bearer ${token}` } }); fetchTrash(); setSuccess('Корзина очищена'); setShowTrash(false); setError(''); } catch (err) { const d = err.response?.data?.detail; setError(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Ошибка очистки корзины')); } }}>Очистить корзину</button>
              <button type="button" className="btn-secondary btn-small" onClick={() => setShowTrash(false)}>Закрыть</button>
            </div>
            <ul className="nas-list nas-trash-list">
              {trashItems.map((t, i) => (
                <li key={t.path + i} className="nas-list-item">
                  <div className="nas-list-item-main">
                    <span className="nas-list-icon">{t.is_dir ? '📁' : '📄'}</span>
                    <div className="nas-trash-item-info">
                      <span className="nas-list-name">{t.name}</span>
                      {t.original_path && t.original_path !== t.name && <span className="nas-trash-original-path">/{t.original_path}</span>}
                    </div>
                    <span className="nas-list-meta">{t.is_dir ? '—' : formatSize(t.size)}</span>
                    <span className="nas-list-meta">{t.deleted_at}</span>
                    <div className="nas-list-actions">
                      <button type="button" className="btn-icon-small" onClick={async () => { const token = localStorage.getItem('token'); if (!token) return; try { await axios.post('/api/nas/trash/restore', null, { params: { path: t.path }, headers: { Authorization: `Bearer ${token}` } }); fetchTrash(); setSuccess('Восстановлено'); setError(''); } catch (err) { const d = err.response?.data?.detail; setError(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Ошибка восстановления')); } }} title="Восстановить">↩</button>
                      <button type="button" className="btn-icon-small" onClick={async () => { const token = localStorage.getItem('token'); if (!token) return; try { await axios.delete('/api/nas/trash', { params: { path: t.path }, headers: { Authorization: `Bearer ${token}` } }); fetchTrash(); setError(''); } catch (err) { const d = err.response?.data?.detail; setError(Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Ошибка удаления')); } }} title="Удалить навсегда">🗑️</button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {trashItems.length === 0 && <p className="nas-empty">Корзина пуста</p>}
          </div>
        </div>
      )}

      {showInfo && nasInfo && (
        <div className="modal-overlay" onClick={() => setShowInfo(false)}>
          <div className="modal nas-info-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Подключение как сетевой диск</h2>
            <p className="nas-info-desc">{nasInfo.credentials_hint || 'Используйте логин и пароль пользователей из раздела «Пользователи приложения Visor-ECO». Один и тот же логин и пароль для входа в приложение, SMB, FTP и WebDAV.'}</p>
            <div className="nas-info-block">
              <label>SMB (Windows, macOS, Linux)</label>
              <div className="nas-info-row">
                <code>{nasInfo.smb_share}</code>
                <button type="button" className="btn-copy" onClick={() => copyToClipboard(nasInfo.smb_share, 'smb')}>{copied === 'smb' ? 'Скопировано' : 'Копировать'}</button>
              </div>
              <p className="nas-info-creds">Логин: <strong>&lt;ваш_логин&gt;</strong>, Пароль: <strong>&lt;ваш_пароль&gt;</strong> — из раздела «Пользователи приложения Visor-ECO». При подключении введите в диалоге Windows (или укажите «Подключить с другими учётными данными»).</p>
            </div>
            <div className="nas-info-block">
              <label>FTP (полный формат: логин и пароль в адресе)</label>
              <div className="nas-info-row">
                <code>{nasInfo.ftp_url_with_placeholders || ('ftp://LOGIN:PASSWORD@' + (nasInfo.host || nasInfo.ftp_host?.replace(/^ftp:\/\//, '')))}</code>
                <button type="button" className="btn-copy" onClick={() => copyToClipboard(nasInfo.ftp_url_with_placeholders || ('ftp://LOGIN:PASSWORD@' + (nasInfo.host || nasInfo.ftp_host?.replace(/^ftp:\/\//, ''))), 'ftp')}>{copied === 'ftp' ? 'Скопировано' : 'Копировать'}</button>
              </div>
              <p className="nas-info-creds">Замените <strong>LOGIN</strong> и <strong>PASSWORD</strong> на свой логин и пароль из раздела «Пользователи приложения Visor-ECO».</p>
            </div>
            <div className="nas-info-block">
              <label>WebDAV</label>
              <div className="nas-info-row">
                <code>{nasInfo.webdav_url}</code>
                <button type="button" className="btn-copy" onClick={() => copyToClipboard(nasInfo.webdav_url, 'webdav')}>{copied === 'webdav' ? 'Скопировано' : 'Копировать'}</button>
              </div>
              <p className="nas-info-creds">Логин: <strong>&lt;ваш_логин&gt;</strong>, Пароль: <strong>&lt;ваш_пароль&gt;</strong> — те же, что в разделе «Пользователи». При подключении сетевого диска введите их в мастере Windows.</p>
            </div>
            {nasInfo.nfs_export && (
              <div className="nas-info-block">
                <label>NFS (Linux)</label>
                <div className="nas-info-row">
                  <code>{nasInfo.nfs_export}</code>
                  <button type="button" className="btn-copy" onClick={() => copyToClipboard(nasInfo.nfs_export, 'nfs')}>{copied === 'nfs' ? 'Скопировано' : 'Копировать'}</button>
                </div>
                <p className="nas-info-creds">На клиенте Linux при монтировании используйте опции для доступа (например, <code>user</code> или настройте NFS-сервер под нужных пользователей).</p>
              </div>
            )}
            <div className="nas-info-block nas-info-windows">
              <label>Подключение с Windows 10/11</label>
              <ul className="nas-info-list">
                <li><strong>SMB:</strong> Проводник → «Этот компьютер» → «Подключить сетевой диск» → укажите путь <code>{nasInfo.smb_share}</code> → включите «Подключить с другими учётными данными» и введите логин и пароль пользователя Visor-ECO.</li>
                <li><strong>WebDAV:</strong> «Подключить сетевой диск» → «Подключиться к сайту» → введите адрес <code>{nasInfo.webdav_url}</code>. Если появляется ошибка, включите Basic-аутентификацию: в реестре <code>HKEY_LOCAL_MACHINE\SYSTEM\CurrentControlSet\Services\WebClient\Parameters</code> установите <code>BasicAuthLevel</code> = 2 и перезапустите службу «WebClient».</li>
                <li><strong>FTP:</strong> В проводнике можно добавить сетевое расположение (FTP) и указать адрес в формате <code>ftp://LOGIN:PASSWORD@{nasInfo.host || ''}</code> или ввести логин/пароль при запросе.</li>
              </ul>
            </div>
            <div className="nas-info-block nas-info-other">
              <label>Подключение с macOS, iOS, Android и Linux</label>
              <ul className="nas-info-list">
                <li><strong>macOS:</strong> Finder → «Переход» → «Подключение к серверу» (или Cmd+K) → введите <code>smb://{nasInfo.host || ''}/visor-nas</code>, затем логин и пароль из раздела «Пользователи». WebDAV: тот же пункт меню, укажите <code>{nasInfo.webdav_url}</code>. FTP — через терминал или приложения (Cyberduck, FileZilla).</li>
                <li><strong>iOS (iPhone/iPad):</strong> Приложение «Файлы» → «Обзор» → ⋯ → «Подключиться к серверу» → введите <code>smb://{nasInfo.host || ''}</code> (или <code>smb://{nasInfo.host || ''}/visor-nas</code>), затем логин и пароль из раздела «Пользователи». Для WebDAV и FTP используйте приложения из App Store (например, FileBrowser, Documents) — в приложении «Файлы» WebDAV не поддерживается. В iOS 18 при появлении только чтения для SMB переподключитесь к серверу или проверьте сеть.</li>
                <li><strong>Android:</strong> Файловый менеджер с поддержкой сети (SambaLite, MiXplorer, Total Commander, X-plore, Cx File Explorer и др.) → добавьте сервер SMB (адрес <code>{nasInfo.host || ''}</code>, ресурс <code>visor-nas</code>), WebDAV (<code>{nasInfo.webdav_url}</code>) или FTP (<code>ftp://{nasInfo.host || ''}</code>) и введите логин и пароль пользователя Visor-ECO.</li>
                <li><strong>Linux:</strong> <strong>SMB:</strong> <code>mount -t cifs //{nasInfo.host || ''}/visor-nas /mnt/nas -o username=LOGIN,password=PASSWORD</code> (или установите <code>cifs-utils</code> и укажите учётные данные). В проводнике (Nautilus, Dolphin): «Другие места» → «Подключение к серверу» → SMB → <code>{nasInfo.smb_share}</code>. <strong>WebDAV:</strong> <code>sudo mount -t davfs {nasInfo.webdav_url} /mnt/nas</code> (пакет <code>davfs2</code>), при запросе введите логин и пароль. <strong>FTP:</strong> поддержка в проводниках и в терминале (curl, lftp).</li>
              </ul>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowInfo(false)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}

      {showHelp && (
        <div className="modal-overlay" onClick={() => setShowHelp(false)}>
          <div className="modal nas-help-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Помощь: подключение к NAS-серверу Visor-ECO</h2>
            <p className="nas-help-intro">Учётные данные для всех протоколов — логин и пароль пользователей из раздела <strong>«Пользователи приложения Visor-ECO»</strong>. Адрес сервера: <code>{nasInfo?.host || '—'}</code> (при необходимости нажмите «Подключение как сетевой диск» для отображения адресов).</p>

            <div className="nas-help-section">
              <h3>🪟 Windows 10 / 11</h3>
              <p className="nas-help-recommended"><strong>Рекомендуемый протокол:</strong> SMB (нативная поддержка, буква диска, стабильная работа).</p>
              <p className="nas-help-sys">Настройки системы:</p>
              <ul>
                <li>Сеть: профиль «Частная», включены «Обнаружение сети» и «Общий доступ к файлам и принтерам».</li>
                <li>Брандмауэр: разрешить «Общий доступ к файлам и принтерам» для частной сети.</li>
                <li>Учётные данные: при первом подключении указать «Подключить с другими учётными данными» и ввести логин/пароль Visor-ECO.</li>
              </ul>
              <p className="nas-help-protocol"><strong>SMB:</strong> Проводник → «Этот компьютер» → «Подключить сетевой диск» → путь <code>\\\\{nasInfo?.host || 'IP'}\\visor-nas</code> → включить «Подключить с другими учётными данными» → ввести логин и пароль.</p>
              <p className="nas-help-protocol"><strong>WebDAV:</strong> «Подключить сетевой диск» → «Подключиться к сайту» → URL <code>{nasInfo?.webdav_url || 'https://IP/api/webdav/'}</code>. При ошибке: реестр → <code>HKEY_LOCAL_MACHINE\\SYSTEM\\CurrentControlSet\\Services\\WebClient\\Parameters</code> → <code>BasicAuthLevel</code> = 2 → перезапуск службы «WebClient».</p>
              <p className="nas-help-protocol"><strong>FTP:</strong> Проводник → «Добавить сетевое расположение» → FTP → <code>ftp://{nasInfo?.host || 'IP'}</code> → указать логин и пароль при запросе.</p>
            </div>

            <div className="nas-help-section">
              <h3>🍎 macOS</h3>
              <p className="nas-help-recommended"><strong>Рекомендуемый протокол:</strong> SMB или WebDAV (встроенная поддержка в Finder).</p>
              <p className="nas-help-sys">Настройки системы:</p>
              <ul>
                <li>Сеть: компьютер и NAS в одной сети (Wi‑Fi или Ethernet).</li>
                <li>Дополнительно не требуется — Finder поддерживает SMB и WebDAV по умолчанию.</li>
              </ul>
              <p className="nas-help-protocol"><strong>SMB:</strong> Finder → «Переход» → «Подключение к серверу» (Cmd+K) → <code>smb://{nasInfo?.host || 'IP'}/visor-nas</code> → «Подключить» → имя и пароль пользователя Visor-ECO.</p>
              <p className="nas-help-protocol"><strong>WebDAV:</strong> Тот же пункт «Подключение к серверу» → <code>{nasInfo?.webdav_url || 'https://IP/api/webdav/'}</code> → ввести учётные данные.</p>
              <p className="nas-help-protocol"><strong>FTP:</strong> Через терминал (<code>ftp {nasInfo?.host || 'IP'}</code>) или приложения Cyberduck, FileZilla с адресом <code>ftp://{nasInfo?.host || 'IP'}</code> и логином/паролем.</p>
            </div>

            <div className="nas-help-section">
              <h3>🐧 Linux</h3>
              <p className="nas-help-recommended"><strong>Рекомендуемый протокол:</strong> SMB (cifs-utils) или WebDAV (davfs2) для монтирования; в проводнике — SMB.</p>
              <p className="nas-help-sys">Настройки системы:</p>
              <ul>
                <li>Установить клиенты: <code>sudo apt install cifs-utils davfs2</code> (или аналог для вашего дистрибутива).</li>
                <li>Создать точку монтирования, например: <code>sudo mkdir -p /mnt/visor-nas</code>.</li>
              </ul>
              <p className="nas-help-protocol"><strong>SMB:</strong> Терминал: <code>sudo mount -t cifs //{nasInfo?.host || 'IP'}/visor-nas /mnt/visor-nas -o username=LOGIN,password=PASSWORD</code>. Или проводник (Nautilus/Dolphin): «Другие места» → «Подключение к серверу» → SMB → <code>smb://{nasInfo?.host || 'IP'}/visor-nas</code>.</p>
              <p className="nas-help-protocol"><strong>WebDAV:</strong> <code>sudo mount -t davfs {nasInfo?.webdav_url || 'https://IP/api/webdav/'} /mnt/visor-nas</code> — при запросе ввести логин и пароль.</p>
              <p className="nas-help-protocol"><strong>FTP:</strong> Проводник или <code>curlftpfs</code>, <code>lftp</code> с адресом <code>ftp://{nasInfo?.host || 'IP'}</code>.</p>
              <p className="nas-help-protocol"><strong>NFS:</strong> При экспорте на сервере: <code>sudo mount -t nfs {nasInfo?.host || 'IP'}:/путь/к/nas /mnt/visor-nas</code> (путь уточните в разделе «Подключение как сетевой диск»).</p>
            </div>

            <div className="nas-help-section">
              <h3>📱 iOS (iPhone / iPad)</h3>
              <p className="nas-help-recommended"><strong>Рекомендуемый протокол:</strong> SMB (приложение «Файлы»).</p>
              <p className="nas-help-sys">Настройки системы:</p>
              <ul>
                <li>Устройство в той же Wi‑Fi сети, что и сервер Visor-ECO.</li>
                <li>В iOS 18 при появлении только чтения для SMB — переподключиться к серверу или обновить iOS.</li>
              </ul>
              <p className="nas-help-protocol"><strong>SMB:</strong> «Файлы» → «Обзор» → ⋯ → «Подключиться к серверу» → <code>smb://{nasInfo?.host || 'IP'}/visor-nas</code> (или <code>smb://{nasInfo?.host || 'IP'}</code>) → имя и пароль пользователя Visor-ECO.</p>
              <p className="nas-help-protocol"><strong>WebDAV / FTP:</strong> В приложении «Файлы» не поддерживаются. Использовать приложения из App Store: FileBrowser, Documents, FE File Explorer и др. — добавить сервер WebDAV или FTP с адресом и учётными данными Visor-ECO.</p>
            </div>

            <div className="nas-help-section">
              <h3>🤖 Android</h3>
              <p className="nas-help-recommended"><strong>Рекомендуемый протокол:</strong> SMB или WebDAV (через файловый менеджер с сетевым доступом).</p>
              <p className="nas-help-sys">Настройки системы:</p>
              <ul>
                <li>Устройство в той же сети, что и сервер.</li>
                <li>Установить файловый менеджер с поддержкой сети: SambaLite, MiXplorer, Total Commander, X-plore, Cx File Explorer и др.</li>
              </ul>
              <p className="nas-help-protocol"><strong>SMB:</strong> В менеджере добавить сервер SMB: хост <code>{nasInfo?.host || 'IP'}</code>, ресурс <code>visor-nas</code>, логин и пароль Visor-ECO.</p>
              <p className="nas-help-protocol"><strong>WebDAV:</strong> Добавить хранилище WebDAV: URL <code>{nasInfo?.webdav_url || 'https://IP/api/webdav/'}</code>, те же учётные данные.</p>
              <p className="nas-help-protocol"><strong>FTP:</strong> Добавить сервер FTP: <code>ftp://{nasInfo?.host || 'IP'}</code>, логин и пароль.</p>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowHelp(false)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NASServer;
